import axios, { AxiosError } from 'axios';
import { Dialog, Toast /* , Dialog */ } from 'vant';
import FullscreenLoading from '@/components/loading/fullscreen';
import { BizCode } from '@/shared/constants';
import { detectEnv } from '@/shared/detect';
import yxhJsBridge from '@/shared/yxhJsBridge';
import { isPlainObject } from 'lodash';
import * as redirector from '@/shared/redirector';
import { genABTestKey } from './abtest';
import PUBLIC_KEY from '@/publicKey';
import { useUserStore } from '@/stores/modules/user';
import { useUTMStore } from '@/stores/modules/utm';
import { useSettingsStore } from '@/stores/modules/settings';
import SATracking from './saTracking';

interface ResponseData {
  status?: BizCode;
  msg?: string;
  data?: any;
}

const BIZ_ERROR = 'BizError';

const instance = axios.create({
  baseURL: `${process.env.VUE_APP_PREFIX}api/v1`,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
});

/**
 * 控制台统一输出异常
 * @param {AxiosError} error
 */
function outputError(error: AxiosError) {
  const { config, response } = error;

  // eslint-disable-next-line no-console
  console.error(
    '%s\n url: %s\n method: %s\n params: %s\n response: %s',
    error.message,
    config.url,
    config.method,
    JSON.stringify(config.data || config.params || null),
    JSON.stringify(response?.data),
  );
}

// 用于判断在APP中是否处于登录状态，防止在APP中多次拉起登录
let loggingInApp = false;
/**
 * 跳转登录页
 */
function gotoLogin() {
  if (detectEnv.yxh) {
    if (loggingInApp) {
      return;
    }

    loggingInApp = true;

    if (
      process.env.VUE_APP_RUNTIME_ENV !== 'prod' &&
      process.env.VUE_APP_RUNTIME_ENV !== 'staging'
    ) {
      Dialog.alert({
        message:
          '请注意当前登录窗口由接口返回登录失效而唤起，如果是非预期行为，请在调用接口前检查登录状态',
        confirmButtonText: '我已知晓',
      }).then(() => {
        yxhJsBridge.close();
      });
    }

    yxhJsBridge.launchLogin().finally(() => {
      loggingInApp = false;
    });
    return;
  }

  const pathname = window.location.pathname.replace(process.env.VUE_APP_PREFIX, '/');

  // 若当前是登录页则重新刷新
  // 理论上这个情况不存在
  if (pathname === '/login') {
    window.__g_app__.forceReload();
    return;
  }

  const { destType } = useSettingsStore();
  const { source } = useUTMStore();
  const { search, hash } = window.location;
  const fullPath = pathname + search + hash;

  window.__g_router__.replace({
    path: '/login',
    query: {
      utm_source: source,
      destType: destType,
      redirect: redirector.generate(fullPath),
    },
  });
}

/**
 * APP版本信息
 */
instance.interceptors.request.use(async (config) => {
  if (detectEnv.yxh) {
    const { appInfo } = useSettingsStore();
    const { osType, version, channel, sysVersion } = appInfo ?? {};

    if (config.headers && typeof version === 'string') {
      config.headers['X-AppVersion'] = version;

      if (osType) {
        config.headers['X-Platform'] = osType;
      }

      if (channel) {
        config.headers['X-Channel'] = channel;
      }
      //增加系统版本
      if (sysVersion) {
        config.headers['X-SystemVersion'] = sysVersion;
      }
    }
  }

  return config;
});

/**
 * 用户信息设置 & 接口请求埋点上报
 */
instance.interceptors.request.use((config) => {
  const loading = config.__interceptorOptions__?.loading ?? true;

  if (loading) {
    FullscreenLoading.show();
  }

  const { token, actToken, isLogin } = useUserStore();

  if (config.headers) {
    // 将时间戳发给后端，这个主要是为了防止在一定时间内的重放攻击
    const now = Date.now().toString();
    config.headers['X-Requested-Key'] = now.slice(5) + now.slice(0, 5);

    config.headers['X-ABTest-Key'] = genABTestKey();

    if (isLogin && token != null) {
      config.headers['X-Token'] = token;
    }

    if (isLogin && actToken != null) {
      config.headers['X-Temp-Token'] = actToken;
    }
  }

  return config;
});

/**
 * 参数加密
 */
instance.interceptors.request.use((config) => {
  const security = config.__interceptorOptions__?.security ?? false;

  /**
   * 以下场景设置默认参数
   * 1. 启用了加密
   * 2. 参数没传
   * 3. 请求为POST
   *
   * 之所以这么处理是因为Spring中的 @RequestBody 注解默认为必传，
   * 如果改为 @RequestBody(required = false) 则影响范围较大，因此在拦截器层面对于此场景填充默认参数
   */
  if (security && config.data == null && config.method?.toUpperCase() === 'POST') {
    config.data = {};
  }

  // 仅生产/预发环境或设置了VUE_APP_SECURITY为true才进行加密
  if (
    (process.env.VUE_APP_RUNTIME_ENV === 'prod' ||
      process.env.VUE_APP_RUNTIME_ENV === 'staging' ||
      process.env.VUE_APP_SECURITY === 'true') &&
    security
  ) {
    try {
      const { aesKey, data } = CECryptojs.RSAWithAES.encrypt(
        PUBLIC_KEY,
        JSON.stringify(config.data ?? {}),
        'AES-CBC',
      );

      config.__aesKey__ = aesKey;
      config.data = data;
    } catch {}
  }

  return config;
});

/**
 * 返回数据解密
 */
instance.interceptors.response.use((response) => {
  const security = response.config.__interceptorOptions__?.security ?? false;

  // 仅生产/预发环境或设置了VUE_APP_SECURITY为true才进行解密
  if (
    (process.env.VUE_APP_RUNTIME_ENV === 'prod' ||
      process.env.VUE_APP_RUNTIME_ENV === 'staging' ||
      process.env.VUE_APP_SECURITY === 'true') &&
    security
  ) {
    try {
      const aesKey = response.config.__aesKey__ ?? '';
      const { ciphertext, nonce } = response.data ?? {};
      response.data = JSON.parse(CECryptojs.AES.decrypt(aesKey, nonce, ciphertext, 'AES-CBC'));
    } catch {}
  }

  return response;
});

/**
 * 处理业务状态码
 */
instance.interceptors.response.use(
  (response) => {
    FullscreenLoading.hide();
    const result: ResponseData = response.data;

    if (result.status === BizCode.SUCCESS) {
      return result.data;
    }

    const error = new Error(
      `Request failed with biz status code ${result.status}${result.msg ? `, ${result.msg}` : ''}`,
    );

    error.name = BIZ_ERROR;
    error.response = response;

    const { toast = true, shouldLogin = true } = response.config?.__interceptorOptions__ ?? {};

    if (process.env.NODE_ENV === 'development') {
      outputError(
        new AxiosError(error.message, error.name, response.config, response.request, response),
      );
    }

    // 未登录或登录失效
    if (result.status === BizCode.UNAUTHORIZED) {
      const userStore = useUserStore();
      userStore.logout();

      if (shouldLogin) {
        gotoLogin();
      }
    } else if (toast) {
      Toast.fail(result.msg || ' 未知错误');
    }

    // 接口请求埋点
    SATracking.trackDev({
      desc: '接口请求异常',
      describe: SATracking.serialize({
        active_url: response.config.url,
        result: result,
      }),
    });

    return Promise.reject(error);
  },
  (error: AxiosError) => {
    FullscreenLoading.hide();

    const config = error.config;
    const { toast = true, shouldLogin = true } = config?.__interceptorOptions__ ?? {};

    // 主动取消的请求（AbortController / CancelToken）静默 reject，不弹错误提示
    if (axios.isCancel(error) || config?.signal?.aborted) {
      return Promise.reject(error);
    }

    if (toast && !isPlainObject(error.response)) {
      Toast.fail('遇到了一个未知的错误\n您的网络可能存在问题');
      return Promise.reject(error);
    }

    const { status, statusText } = error.response ?? {};

    if (status === 401) {
      const userStore = useUserStore();
      userStore.logout();

      if (shouldLogin) {
        gotoLogin();
      }
    } else if (toast) {
      if (status === 403) {
        Toast.fail('您没有权限访问');
      } else {
        Toast.fail(statusText || ' 未知错误');
      }
    }

    if (process.env.NODE_ENV === 'development') {
      outputError(error);
    }
    // 接口请求埋点
    SATracking.trackDev({
      desc: '接口请求异常',
      describe: SATracking.serialize({
        active_url: config?.url,
        http_code: status,
      }),
    });

    return Promise.reject(error);
  },
);

export default instance;

// ===================== IM 客服平台专用实例 =====================

export const imAxios = axios.create({
  baseURL:
    process.env.VUE_APP_RUNTIME_ENV === 'prod'
      ? 'https://im.vjia-tech.cn/api/im'
      : `${process.env.VUE_APP_PREFIX}api/im`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});
// IM 响应拦截器
// 后端约定的成功响应：{ success: true, status: '0000', msg: '成功', data: ... }
imAxios.interceptors.response.use(
  (response) => {
    const { success, status, msg, data, code } = response.data || {};
    if (success === true || status === '0000' || code === '0000' || code === 200) {
      return data;
    }
    return Promise.reject(new Error(msg || '请求失败'));
  },
  (error) => {
    const message = error.response?.data?.msg || error.message || '未知错误';
    return Promise.reject(new Error(message));
  },
);
